import { Feature } from '@mnd-frontend/graphql-schema';
import { useCheckNewNavigationDisabled } from '@mnd-frontend/hooks';
import {
  Button,
  Flex,
  GREY_200,
  GREY_300,
  GREY_800,
  GREY_1000,
  NEUTRAL_WHITE,
  PURPLE_700,
  SPACING_1_5,
  SPACING_3,
  SPACING_5,
  SPACING_12,
  media,
  useToasts,
} from '@mnd-frontend/ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useCurrentPressroom } from '../../contexts/CurrentPressroomContext';
import { useReportsAccessQuery, useRequestTrialMutation } from '../../generated/hooks';
import { track } from '../../lib/tracker';
import routes from '../../routes';
import boxImage from './assets/reports-box.png';
import upsellImage1 from './assets/upsell-1.webp';
import upsellImage2 from './assets/upsell-2.webp';
import upsellImage3 from './assets/upsell-3.webp';
import upsellImage4 from './assets/upsell-4.webp';
import upsellImage5 from './assets/upsell-5.webp';

const Page = styled.div<{ $newNavigationDisabled: boolean }>`
  background-color: ${({ $newNavigationDisabled }) =>
    $newNavigationDisabled ? GREY_200 : 'transparent'};
`;

const Wrapper = styled.div`
  padding-top: ${SPACING_12};
  max-width: 57.75rem;
  margin: 0 auto;

  ${media.lessThan('desktop')} {
    padding-left: ${SPACING_1_5};
    padding-right: ${SPACING_1_5};
  }

  h1 {
    font-size: 3rem;
    line-height: 3.625rem;
  }

  p {
    font-size: 1.25rem;
    line-height: 1.5rem;
    margin-bottom: ${SPACING_3};
  }
`;

const SecondaryWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;

  ${media.lessThan('tablet')} {
    padding-top: ${SPACING_3};

    h1 {
      font-size: 2rem;
      line-height: 2.5rem;
    }
  }
`;

const ContentWrapper = styled(Wrapper)`
  margin-top: -11rem;
`;

const Panel = styled.div<{ $newNavigationDisabled: boolean }>`
  padding-bottom: 10.5rem;
  background-color: ${({ $newNavigationDisabled }) =>
    $newNavigationDisabled ? NEUTRAL_WHITE : 'transparent'};
`;

const RowWrapper = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${SPACING_3};

  ${media.lessThan('desktop')} {
    flex-direction: column;
  }
`;

const Feature1 = styled(Flex)<{ $variant: 'light' | 'dark'; $width: number }>`
  flex-direction: column;
  background-color: ${({ $variant }) => ($variant === 'light' ? 'white' : GREY_1000)};
  padding: ${SPACING_3};
  padding-top: ${SPACING_5};
  padding-bottom: 0;
  margin-right: ${SPACING_3};
  width: ${({ $width }) => $width}%;
  height: 34.75rem;

  ${media.lessThan('desktop')} {
    margin-right: 0;
    margin-bottom: ${SPACING_3};
    width: 100%;
  }

  h2 {
    color: ${({ $variant }) => ($variant === 'light' ? GREY_1000 : 'white')};
    margin-top: 0;
    margin-bottom: ${SPACING_1_5};
    font-size: 2rem;
    line-height: 2.5rem;
  }

  p {
    color: ${({ $variant }) => ($variant === 'light' ? GREY_800 : NEUTRAL_WHITE)};
    margin-bottom: ${({ $variant }) => ($variant === 'light' ? SPACING_12 : SPACING_5)};
    font-size: 1rem;
  }
`;

const Feature2 = styled(Flex)<{ $width: number }>`
  flex-direction: column;
  background-color: ${NEUTRAL_WHITE};
  width: ${({ $width }) => $width}%;
  height: 34.75rem;

  ${media.lessThan('desktop')} {
    width: 100%;
  }

  h2 {
    margin: 0;
    font-size: 2rem;
    line-height: 2.5rem;
    margin-bottom: ${SPACING_1_5};
    padding-top: ${SPACING_5};
    padding-left: ${SPACING_3};
    padding-right: ${SPACING_3};
  }

  p {
    padding-left: ${SPACING_3};
    padding-right: ${SPACING_3};
    color: ${GREY_800};
    font-size: 1rem;
  }
`;

const FullWidthFeature = styled(Flex)`
  width: 100%;
  background-color: ${PURPLE_700};
  height: 18.75rem;
  padding: ${SPACING_3};
  padding-top: ${SPACING_5};
  padding-bottom: 0;
  margin-bottom: ${SPACING_12};

  ${media.lessThan('desktop')} {
    flex-direction: column;
    height: auto;
  }

  h2 {
    color: ${NEUTRAL_WHITE};
    margin-top: 0;
    margin-bottom: ${SPACING_1_5};
    font-size: 2rem;
    line-height: 2.5rem;
  }

  p {
    color: ${NEUTRAL_WHITE};
    font-size: 1rem;
    max-width: calc(100% - 2.25rem);
  }
`;

const upsellImageDefaults = ({ $src }: { $src: string }) => `
  width: 100%;
  height: 100%;
  background-image: url(${$src});
  background-repeat: no-repeat;
`;

const UpsellImage1 = styled.div<{ $src: string }>`
  ${({ $src }) => upsellImageDefaults({ $src })}
  background-size: cover;
  margin-top: auto;
`;

const UpsellImage2 = styled.div<{ $src: string }>`
  ${({ $src }) => upsellImageDefaults({ $src })}
  background-size: cover;
  background-position-y: 50%;
`;

const UpsellImage3 = styled.div<{ $src: string }>`
  ${({ $src }) => upsellImageDefaults({ $src })}
  background-size: cover;
  box-shadow: 0 0 24px ${GREY_300};
`;

const UpsellImage4 = styled.div<{ $src: string }>`
  ${({ $src }) => upsellImageDefaults({ $src })}
  background-size: contain;
  background-position-y: 50%;
  background-size: 110%;
  background-position-x: 50%;
`;

const UpsellImage5 = styled.div<{ $src: string }>`
  ${({ $src }) => upsellImageDefaults({ $src })}
  background-size: cover;
  background-position-y: 50%;
`;

const UpsellImage6 = styled.div<{ $src: string }>`
  ${({ $src }) => upsellImageDefaults({ $src })}
  background-size: contain;
  background-position-x: 50%;
  height: 26.875rem;

  ${media.lessThan('tablet')} {
    height: 14.375rem;
  }
`;

export const ReportUpsellPage = () => {
  const { t } = useTranslation();
  const { pressroomId } = useCurrentPressroom();
  const { showFailureToast } = useToasts();
  const newNavigationDisabled = useCheckNewNavigationDisabled();

  useEffect(() => {
    track({ event: 'Reports: Viewed Upsell Page' });
  }, []);

  const { data, loading } = useReportsAccessQuery({
    variables: {
      pressroomId,
    },
    onError: () => showFailureToast(),
  });
  const navigate = useNavigate();
  const [requestTrial, { loading: requestingTrial }] = useRequestTrialMutation({
    variables: {
      pressroomId,
      features: [Feature.BrandReports, Feature.MonitorRadio],
    },
    onCompleted: response => {
      if (response.featureRequestTrial2?.error) {
        showFailureToast(response.featureRequestTrial2.error.message);
      } else {
        navigate(routes.reportsList({ pressroomId }));
      }
    },
  });

  return (
    <Page $newNavigationDisabled={newNavigationDisabled}>
      <Panel $newNavigationDisabled={newNavigationDisabled}>
        <Wrapper>
          <h1>{t('reports_upsell_primary_title')}</h1>
          <p>{t('reports_upsell_primary_description')}</p>
          {!data?.pressroom?.source?.features.brandReport?.previousTrial && (
            <Button
              onClick={() => {
                requestTrial();
                track({ event: 'Reports: Clicked Start Trial' });
              }}
              type="button"
              loading={loading || requestingTrial}
            >
              {t('reports_upsell_start_free_trial')}
            </Button>
          )}
        </Wrapper>
      </Panel>
      <ContentWrapper>
        <div>
          <RowWrapper>
            <Feature1 $variant="dark" $width={60}>
              <h2>{t('reports_upsell_feature_1_title')}</h2>
              <p>{t('reports_upsell_feature_1_description')}</p>
              <UpsellImage1 $src={upsellImage1} />
            </Feature1>
            <Feature2 $width={40}>
              <UpsellImage2 $src={upsellImage4} />
              <h2>{t('reports_upsell_feature_2_title')}</h2>
              <p>{t('reports_upsell_feature_2_description')}</p>
            </Feature2>
          </RowWrapper>
          <RowWrapper>
            <Feature1 $variant="light" $width={50}>
              <h2>{t('reports_upsell_feature_3_title')}</h2>
              <p>{t('reports_upsell_feature_3_description')}</p>
              <UpsellImage3 $src={upsellImage2} />
            </Feature1>
            <Feature2 $width={50}>
              <UpsellImage4 $src={upsellImage3} />
              <h2>{t('reports_upsell_feature_4_title')}</h2>
              <p>{t('reports_upsell_feature_4_description')}</p>
            </Feature2>
          </RowWrapper>
          <FullWidthFeature>
            <div>
              <h2>{t('reports_upsell_feature_5_title')}</h2>
              <p>{t('reports_upsell_feature_5_description')}</p>
            </div>
            <UpsellImage5 $src={upsellImage5} />
          </FullWidthFeature>
        </div>
      </ContentWrapper>
      <Panel $newNavigationDisabled={newNavigationDisabled}>
        <SecondaryWrapper>
          <UpsellImage6 $src={boxImage} />
          <h1>{t('reports_upsell_secondary_title')}</h1>
          {!data?.pressroom?.source?.features.brandReport?.previousTrial && (
            <Button
              onClick={() => {
                requestTrial();
                track({ event: 'Reports: Clicked Start Trial' });
              }}
              type="button"
              loading={loading || requestingTrial}
            >
              {t('reports_upsell_start_free_trial')}
            </Button>
          )}
        </SecondaryWrapper>
      </Panel>
    </Page>
  );
};
